import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useModal } from '../../contexts/ModalContext'
import { challengePaymentSuccessPath, challengeTeamCreatePath } from '../../navigation'
import { PassType, TeamData } from '../../service/domain/PaymentService'
import { PaymentAction } from '../../screens/PaymentSuccessScreen'
import PassCheckoutModal from '../../ui/payment/PassCheckoutModal'
import { useChallenge } from '../challenge/useChallenge'
import { useChallengeContext } from '../challenge/useChallengeContext'
import { useChallengeId } from '../challenge/useChallengeId'
import { useChallengeAccess } from '../membership/useChallengeAccess'
import { useConfig } from '../util/useConfig'
import { buildPaymentReturnUrl, needsAppUpdateForCheckout } from './paymentDeepLink'
import { useCreateCheckoutSession } from './useCreateCheckoutSession'

export function usePaymentIntro() {
  const navigate = useNavigate()
  const challengeId = useChallengeId()
  const [searchParams] = useSearchParams()
  const sourcePath = searchParams.get('sourcePath')
  const actionParam = searchParams.get('action')
  const action = actionParam ? (JSON.parse(actionParam) as PaymentAction) : undefined
  const isCreatingTeam = !!challengeId && sourcePath === challengeTeamCreatePath(challengeId)

  const { isEntitled, refetchEntitlement } = useChallengeAccess(challengeId)
  const { data: config, isLoading: isLoadingConfig, error: configError } = useConfig(challengeId)
  const { data: challenge, isLoading: isLoadingChallenge } = useChallenge(challengeId)
  const { data: challengeContext } = useChallengeContext(challengeId)
  const { openModal, closeModal } = useModal()
  const { mutateAsync: createCheckoutSession } = useCreateCheckoutSession()
  const isWeb = Capacitor.getPlatform() === 'web'

  useEffect(() => {
    refetchEntitlement()
    if (isEntitled && challengeId) {
      const successParams = new URLSearchParams()
      successParams.set('sourcePath', sourcePath || '')
      successParams.set('action', JSON.stringify(action))
      successParams.set(
        'passType',
        action?.type === 'CREATE_TEAM' ? PassType.TEAM : PassType.INDIVIDUAL,
      )
      navigate(`${challengePaymentSuccessPath(challengeId)}?${successParams.toString()}`, {
        replace: true,
      })
    }
  }, [isEntitled, challengeId, navigate, sourcePath, action, refetchEntitlement])

  async function proceedToCheckout(passType: PassType) {
    if (!sourcePath) throw new Error('No source path provided')
    if (!config) throw new Error('Config not loaded')
    if (!challengeId) throw new Error('No challenge')

    let teamData: TeamData | undefined
    if (
      (passType === PassType.TEAM || passType === PassType.LEADER) &&
      action?.type === 'CREATE_TEAM'
    ) {
      const payload = action.payload as { name: string }
      teamData = {
        name: payload.name,
        targetMemberCount: challengeContext?.teamSizeTarget,
      }
    }

    let teamId: string | undefined
    if (passType === PassType.INDIVIDUAL && action?.type === 'JOIN_TEAM') {
      teamId = action.payload.teamId as string
    }

    const successParams = new URLSearchParams()
    successParams.set('sourcePath', sourcePath)
    successParams.set('action', JSON.stringify(action))
    successParams.set('passType', passType)

    const successPath = `${challengePaymentSuccessPath(challengeId)}?${successParams.toString()}`
    const cancelPath = `${window.location.pathname}${window.location.search}`

    const successUrl = isWeb
      ? `${window.location.origin}${successPath}`
      : buildPaymentReturnUrl(successPath)
    const cancelUrl = isWeb
      ? `${window.location.origin}${cancelPath}`
      : buildPaymentReturnUrl(cancelPath)

    const url = await createCheckoutSession({
      passType,
      config,
      successUrl,
      cancelUrl,
      teamData,
      teamId,
      actionType: action?.type,
      challengeId,
    })

    if (isWeb) {
      window.location.href = url
    } else {
      await Browser.open({ url, presentationStyle: 'popover' })
    }
  }

  function openCheckoutModal(passType: 'individual' | 'team' | 'leader') {
    const modalId = `checkout-modal-${passType}`
    const passTypeEnum =
      passType === 'individual'
        ? PassType.INDIVIDUAL
        : passType === 'team'
          ? PassType.TEAM
          : PassType.LEADER

    openModal(
      modalId,
      <PassCheckoutModal
        passType={passType}
        shirtImageUrl={challenge?.shirtImageUrl}
        onClose={() => closeModal(modalId)}
        onProceed={() => proceedToCheckout(passTypeEnum)}
      />,
    )
  }

  return {
    challengeId,
    sourcePath,
    action,
    isCreatingTeam,
    config,
    isLoadingConfig: isLoadingConfig || isLoadingChallenge,
    configError,
    challengeContext,
    isWeb,
    needsAppUpdate: needsAppUpdateForCheckout(),
    openCheckoutModal,
  }
}
